import { useState, useEffect } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Paper, Tab, Tabs, Badge, Typography, Switch
} from '@mui/material';
import {
  Home as HomeIcon, Message as MessageIcon, CalendarToday,
  People as PeopleIcon, Person as PersonIcon, Settings as SettingsIcon,
  AccountBalanceWallet, Visibility, VisibilityOff, MenuBook
} from '@mui/icons-material';
import { getCurrentLanguage } from '../../utils/navigation';

const TeacherLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const lang = getCurrentLanguage();

  const [activeTab, setActiveTab] = useState('home');
  const [profileVisible, setProfileVisible] = useState(true);

  const tabs = [
    { id: 'home', label: t('teacher.nav.home', 'Home'), icon: <HomeIcon /> },
    { id: 'lessons', label: 'Lessons', icon: <MenuBook /> },
    { id: 'messages', label: t('teacher.nav.messages', 'Messages'), icon: <MessageIcon /> },
    { id: 'calendar', label: t('teacher.nav.calendar', 'Calendar'), icon: <CalendarToday /> },
    { id: 'students', label: t('teacher.nav.students', 'Students'), icon: <PeopleIcon /> },
    { id: 'earnings', label: t('teacher.nav.earnings', 'Earnings'), icon: <AccountBalanceWallet /> },
    { id: 'profile', label: t('teacher.nav.myProfile', 'My Profile'), icon: <PersonIcon /> },
    { id: 'settings', label: t('teacher.nav.settings', 'Settings'), icon: <SettingsIcon /> },
  ];

  useEffect(() => {
    const path = location.pathname;
    const tabMatch = tabs.find(tab => path.includes(`/teacher/${tab.id}`));
    if (tabMatch) {
      setActiveTab(tabMatch.id);
    } else if (path.endsWith('/dashboard') || path.endsWith('/teacher')) {
      setActiveTab('home');
    }
  }, [location.pathname]);

  const handleTabChange = (_, tabId) => {
    setActiveTab(tabId);
    if (tabId === 'home') {
      navigate(`/${lang}/teacher/dashboard`);
    } else {
      navigate(`/${lang}/teacher/${tabId}`);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', pt: 8 }}>
      {/* Sticky Tab Navigation */}
      <Paper
        sx={{
          position: 'sticky', top: 64, zIndex: 100,
          borderRadius: 0, borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 56,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.875rem'
                },
                '& .Mui-selected': { fontWeight: 700 }
              }}
            >
              {tabs.map(tab => (
                <Tab
                  key={tab.id}
                  value={tab.id}
                  label={tab.label}
                  icon={tab.id === 'messages' ? (
                    <Badge badgeContent={0} color="error">{tab.icon}</Badge>
                  ) : tab.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, pr: 2 }}>
              {profileVisible ? <Visibility fontSize="small" color="action" /> : <VisibilityOff fontSize="small" color="action" />}
              <Typography variant="body2" color="text.secondary">
                Profile {profileVisible ? 'visible' : 'hidden'}
              </Typography>
              <Switch
                checked={profileVisible} size="small"
                onChange={(e) => setProfileVisible(e.target.checked)}
              />
            </Box>
          </Box>
        </Container>
      </Paper>

      {/* Page Content — rendered by child route */}
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Outlet />
      </Container>
    </Box>
  );
};

export default TeacherLayout;
